import './AutoCashinDialog.less';

import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import rangeSlider from 'rangeslider-pure';

import { Button } from 'nike-ui/src/components/button/Button';
import { useIsCashinDisabled } from 'nike-ui/src/components/ticketus/cashin/useIsCashinDisabled';
import { cashinOfferByTicketId$$ } from 'nike-ui/src/components/turboLive/components/cashin/subs';

import { DialogBody, DialogTitle, Docked } from '@/ui/dialogs/Dialog';
import i18n from '@i18n';
import PopupNg from '@/ui/PopupNg';
import { cashinOfferStatus } from '@/mytickets/constants/cashinOfferStatus';
import { ticketDetail$ } from '@/mytickets/detail/subs';

function useSlider({ onSlide, onSlideEnd, minValue, maxValue, value }) {
    const callbacks = useRef({});
    const field = useRef();
    const container = useRef();
    const element = (
        <div className="nike-slider-container" ref={container}>
            <input
                ref={field}
                className="nike-slider"
                type="range"
                min={minValue}
                max={maxValue}
                step={0.01}
                data-buffer={100}
            />
        </div>
    );
    useEffect(() => {
        callbacks.current.onSlide = onSlide;
        callbacks.current.onSlideEnd = onSlideEnd;
    }, [onSlide, onSlideEnd]);
    useEffect(() => {
        if (field.current.rangeSlider) {
            field.current.rangeSlider.update(
                {
                    value: value
                },
                false
            );
        } else {
            rangeSlider.create(field.current, {
                value: value,
                // toto je nejaka zvratenost (bug) v 'rangeslider-pure', position je v skutocnosti value, co je value netusim
                onSlideStart() {
                    container.current.classList.add('no-animate');
                },
                onSlide(position) {
                    callbacks.current.onSlide(position);
                },
                onSlideEnd(position) {
                    callbacks.current.onSlideEnd(position);
                    container.current.classList.remove('no-animate');
                }
            });
        }
    }, [value, maxValue]);
    return [element];
}

function AutoCashinDialog({ ticketId, cashinPosition }) {
    const { derefSub, subscribe } = useReframe();
    const invokedCashinOffer = subscribe(cashinOfferByTicketId$$(ticketId));
    const { amount, automaticTransactionLimit, ticketId: cashinTicketId, hash } = invokedCashinOffer;
    const automaticTransactionLimitString = automaticTransactionLimit?.toString();
    const cashinOffer = derefSub(['ticketus/cashin-offer', cashinTicketId]);
    const [autoCashin, setAutoCashin] = useState(automaticTransactionLimitString || amount);
    const error = derefSub(['ticketus/auto-cashin-error']);
    const disabled = useIsCashinDisabled(cashinOffer);
    const [errorText, setErrorText] = useState(null);
    const isButtonActive =
        !disabled && autoCashin && parseFloat(autoCashin) !== 0 && autoCashin !== automaticTransactionLimitString;
    const [focused, setFocused] = useState(false);
    const inputRef = useRef();
    const ticket = subscribe(ticketDetail$(hash));
    const max = cashinOffer.get('win');
    const min = cashinOffer.get('amount');
    const pattern = /^((0\.(\d{0,2})?)|([1-9][0-9]{0,5}(\.\d{0,2})?))$/;

    function setCashinValue(e) {
        if (pattern.test(e.target.value) || e.target.value === '' || e.target.value === '0') {
            setAutoCashin(e.target.value);
        }
    }

    const [sliderElement] = useSlider({
        maxValue: max,
        minValue: min,
        value: autoCashin,
        onSlide: value => setAutoCashin(value),
        onSlideEnd: value => setAutoCashin(value)
    });

    useEffect(() => {
        return dispatch(['cashin/cashin-auto/clearError']);
    }, []);
    useEffect(() => {
        if (cashinOffer?.get('status') === cashinOfferStatus.Finished) {
            PopupNg.dispatcher.close();
            if (ticket.winningMethod === 'Cashin') {
                PopupNg.dispatcher.open('cashin/cashin-paidOut-dialog', {
                    cashinTransaction: cashinOffer,
                    closeDialog: PopupNg.dispatcher.close
                });
            } else {
                PopupNg.dispatcher.show({
                    title: i18n('commons.term.odkupH1'),
                    messages: i18n('ticketus.cashin.offer.Finished')
                });
            }
        }
    }, [cashinOffer, ticket]);

    function cancelAutoCashin() {
        PopupNg.dispatcher.show(
            {
                title: i18n('cashin.autoCashinCancelDialog.title'),
                messages: i18n('cashin.autoCashinCancelDialog.text'),
                buttons: [
                    [
                        i18n('commons.button.ano'),
                        [
                            'cashin/cashin-auto/cancel',
                            cashinOffer.get('ticketId'),
                            cashinOffer.get('version'),
                            cashinPosition
                        ]
                    ],
                    [i18n('commons.button.nie')]
                ]
            },
            3
        );
    }

    function createAutoCashin() {
        const autoCashinInt = parseFloat(autoCashin);
        if (autoCashinInt < cashinOffer.get('amount')) {
            return setErrorText(i18n('autoCashin.minValueError', cashinOffer.get('amount')));
        }
        if (autoCashinInt > cashinOffer.get('win')) {
            return setErrorText(i18n('autoCashin.maxValueError', cashinOffer.get('win')));
        }
        setErrorText(null);
        dispatch([
            'cashin/cashin-auto/create',
            cashinOffer.get('ticketId'),
            autoCashinInt,
            cashinOffer.get('version'),
            cashinPosition
        ]);
    }

    return (
        <Docked className="auto-cashin-dialog">
            <DialogTitle>
                <h2>{i18n('cashin.autoCashinDialog.title')}</h2>
            </DialogTitle>
            <DialogBody className="auto-cashin-dialog-body">
                <div className="auto-cashin-dialog-info">
                    {i18n('autoCashin.info')} {Number(autoCashin).toFixed(2) || 0} €? &nbsp;
                    <a
                        onClick={() =>
                            PopupNg.dispatcher.open('dialog/article', { articleCode: 'auto-cashin-info' }, 100)
                        }
                        className="ui-link"
                    >
                        {i18n('autoCashin.info.moreInfo')}
                    </a>
                </div>
                <div className={'auto-cashin-dialog-interval-info'}>
                    {i18n('autoCashin.intervalInfo', cashinOffer?.get('amount') || '-', cashinOffer?.get('win') || '-')}
                </div>
                <div className="flex">
                    <div
                        className={clsx('auto-cashin-dialog-input', {
                            error: error || errorText,
                            active: isButtonActive,
                            focused: focused && !disabled,
                            disabled
                        })}
                        onClick={() => {
                            setFocused(true);
                            inputRef.current.focus();
                        }}
                    >
                        <div>
                            <span className="input-wrap">
                                <span className="width-machine" aria-hidden="true">
                                    {autoCashin ? autoCashin : 0}
                                </span>
                                <input
                                    ref={inputRef}
                                    min="0"
                                    max="300000.00"
                                    className="input"
                                    value={autoCashin}
                                    type="number"
                                    placeholder={0}
                                    onChange={e => setCashinValue(e)}
                                    onBlur={() => setFocused(false)}
                                    disabled={disabled}
                                />
                            </span>
                            €
                        </div>
                    </div>
                    <div className="stake-amount-slider-ng">{sliderElement}</div>
                </div>
                {error || errorText ? (
                    <div className="auto-cashin-dialog-input-error">
                        {errorText ? errorText : translateServerError(error)}
                    </div>
                ) : null}
                <div className="auto-cashin-dialog-buttons">
                    {automaticTransactionLimitString ? (
                        <>
                            <Button
                                className="red"
                                label={i18n('autoCashin.buttonText.cancel')}
                                onClick={cancelAutoCashin}
                                disabled={disabled}
                            />
                            <Button
                                primary
                                disabled={!isButtonActive}
                                label={i18n('autoCashin.buttonText.save')}
                                onClick={createAutoCashin}
                            />
                        </>
                    ) : (
                        <Button
                            primary
                            disabled={!isButtonActive}
                            label={i18n('autoCashin.buttonText.set')}
                            onClick={createAutoCashin}
                        />
                    )}
                </div>
            </DialogBody>
        </Docked>
    );
}

function translateServerError(error) {
    switch (error) {
        case 'DELETE_ERROR':
            return i18n('autoCashin.deleteError');
        default:
            return i18n('autoCashin.defaultError');
    }
}

export default AutoCashinDialog;
